import { GraphQLResult } from 'aws-amplify/api';
import { FilterValue } from '@/API';
import { GenericRepository, PagedList } from '@/repository/GenericRepository';
import { GraphQLOptionsV6 } from '@aws-amplify/api-graphql';
import { listFilterValues, searchFilterValues } from '@/graphql/queries';
import { createFilterValue, updateFilterValue } from '@/graphql/mutations';
import log from 'loglevel';

export class FilterValueRepository extends GenericRepository<FilterValue> {
  /**
   * Gets all active default filter values for a specific list type
   * Always returns the latest version for each fieldId
   */
  async getDefaultFilterValuesByListType(
    listType: string
  ): Promise<FilterValue[]> {
    try {
      const result = (await this.client.graphql({
        query: listFilterValues,
        variables: {
          filter: {
            listType: { eq: listType },
          },
          limit: 1000,
        },
      })) as GraphQLResult<{
        listFilterValues: {
          items: FilterValue[];
        };
      }>;

      const items = result.data?.listFilterValues?.items || [];

      // Group by fieldId and select the latest version for each field
      const latestVersionsByField = this.getLatestVersionsByField(items);

      // Return only the latest versions, sorted by fieldId for consistency
      return Array.from(latestVersionsByField.values()).sort((a, b) =>
        a.fieldId.localeCompare(b.fieldId)
      );
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : JSON.stringify(error);
      log.error('FilterValueRepository error details:', error);
      throw new Error(`Error fetching filter values: ${errorMessage}`);
    }
  }

  /**
   * Gets all default filter values for a specific field
   * Always returns the latest version first
   */
  async getDefaultFilterValuesByField(
    listType: string,
    fieldId: string
  ): Promise<FilterValue[]> {
    try {
      const result = (await this.client.graphql({
        query: listFilterValues,
        variables: {
          filter: {
            and: [{ listType: { eq: listType } }, { fieldId: { eq: fieldId } }],
          },
          limit: 1000,
        },
      })) as GraphQLResult<{
        listFilterValues: {
          items: FilterValue[];
        };
      }>;

      const items = result.data?.listFilterValues?.items || [];

      // Sort by version (higher versions first for latest)
      return items.sort(
        (a, b) => parseInt(b.version || '0') - parseInt(a.version || '0')
      );
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : JSON.stringify(error);
      log.error('FilterValueRepository error details:', error);
      throw new Error(`Error fetching filter values by field: ${errorMessage}`);
    }
  }

  /**
   * Helper method to get the latest version for each fieldId
   */
  private getLatestVersionsByField(
    items: FilterValue[]
  ): Map<string, FilterValue> {
    const latestVersionsByField = new Map<string, FilterValue>();

    items.forEach(item => {
      const currentLatest = latestVersionsByField.get(item.fieldId);

      if (!currentLatest) {
        // First item for this fieldId
        latestVersionsByField.set(item.fieldId, item);
      } else {
        // Compare versions and keep the latest
        const currentVersion = parseInt(currentLatest.version || '0');
        const itemVersion = parseInt(item.version || '0');

        if (itemVersion > currentVersion) {
          latestVersionsByField.set(item.fieldId, item);
        }
      }
    });

    return latestVersionsByField;
  }

  async search(queryVariables: GraphQLOptionsV6['variables']) {
    return (
      await this.client.graphql({
        query: searchFilterValues,
        variables: queryVariables,
      })
    ).data.searchFilterValues;
  }

  /**
   * Lists all filter values with optional filtering
   */
  async list(
    options?: GraphQLOptionsV6['variables']
  ): Promise<PagedList<FilterValue, unknown>> {
    try {
      const result = (await this.client.graphql({
        query: listFilterValues,
        variables: {
          filter: options?.filter || null,
          limit: options?.limit || 1000,
          nextToken: options?.nextToken || null,
        },
      })) as GraphQLResult<{
        listFilterValues: {
          items: FilterValue[];
          nextToken?: string;
        };
      }>;

      return {
        __typename: 'FilterValueConnection' as unknown,
        items: result.data?.listFilterValues?.items || [],
        nextToken: result.data?.listFilterValues?.nextToken || null,
      };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : JSON.stringify(error);
      log.error('FilterValueRepository list error details:', error);
      throw new Error(`Error listing filter values: ${errorMessage}`);
    }
  }

  /**
   * Creates a new FilterValue record
   */
  async save(
    object:
      | FilterValue
      | Omit<
          FilterValue,
          '__typename' | 'id' | 'createdAt' | 'updatedAt' | 'owner'
        >
  ): Promise<FilterValue> {
    try {
      const result = (await this.client.graphql({
        query: createFilterValue,
        variables: {
          input: {
            listType: object.listType,
            fieldId: object.fieldId,
            version: object.version,
            selectedValues: object.selectedValues,
            relationalOperator: object.relationalOperator,
            description: object.description,
            changeReason: object.changeReason,
            changedBy: object.changedBy,
          },
        },
      })) as GraphQLResult<{
        createFilterValue: FilterValue;
      }>;

      if (!result.data?.createFilterValue) {
        throw new Error('Failed to create filter value - no data returned');
      }

      return result.data.createFilterValue;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : JSON.stringify(error);
      log.error('FilterValueRepository save error details:', error);
      throw new Error(`Error saving filter value: ${errorMessage}`);
    }
  }

  /**
   * Updates an existing FilterValue record
   */
  async update(
    object:
      | FilterValue
      | Omit<FilterValue, '__typename' | 'createdAt' | 'updatedAt' | 'owner'>
  ): Promise<FilterValue> {
    try {
      const result = (await this.client.graphql({
        query: updateFilterValue,
        variables: {
          input: {
            id: object.id,
            listType: object.listType,
            fieldId: object.fieldId,
            version: object.version,
            selectedValues: object.selectedValues,
            relationalOperator: object.relationalOperator,
            description: object.description,
            changeReason: object.changeReason,
            changedBy: object.changedBy,
          },
        },
      })) as GraphQLResult<{
        updateFilterValue: FilterValue;
      }>;

      if (!result.data?.updateFilterValue) {
        throw new Error('Failed to update filter value - no data returned');
      }

      return result.data.updateFilterValue;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : JSON.stringify(error);
      log.error('FilterValueRepository update error details:', error);
      throw new Error(`Error updating filter value: ${errorMessage}`);
    }
  }
}
